import { useCallback, useEffect, useRef, useState } from 'react';

export type ParkedQuestion<Q, A> = {
    /**
     * What the dialog shows. The last question stays after it is answered,
     * with only `open` cleared, so the dialog can close with its content.
     */
    question: Q & { open: boolean };
    /**
     * Opens the question and resolves with its answer, or with the fallback
     * once `signal` aborts.
     */
    ask: (question: Q, signal?: AbortSignal) => Promise<A>;
    /** Answers the open question; a no-op when none is open. */
    answer: (answer: A) => void;
};

/**
 * A question the code waits on until the user answers it in a dialog.
 *
 * A question that cannot be answered any more resolves with `fallback`: one
 * still open when the hook unmounts, one asked after that, one replaced by a
 * newer question, and one whose `signal` aborts. Whatever waits on it therefore always continues, and
 * `fallback` must be the answer that changes nothing.
 *
 * @param closed The question shown before any is asked. Must be stable.
 */
export default function useParkedQuestion<Q extends object, A>(
    closed: Q,
    fallback: A
): ParkedQuestion<Q, A> {
    const [question, setQuestion] = useState<Q & { open: boolean }>({
        ...closed,
        open: false,
    });
    const resolverRef = useRef<((answer: A) => void) | null>(null);
    const unmountedRef = useRef(false);

    const answer = useCallback((value: A): void => {
        setQuestion((previous) => ({ ...previous, open: false }));
        const resolve = resolverRef.current;
        resolverRef.current = null;
        resolve?.(value);
    }, []);

    const ask = useCallback(
        (next: Q, signal?: AbortSignal): Promise<A> => {
            if (unmountedRef.current || signal?.aborted) {
                return Promise.resolve(fallback);
            }
            resolverRef.current?.(fallback);
            return new Promise<A>((resolve) => {
                const settle = (value: A): void => {
                    signal?.removeEventListener('abort', withdraw);
                    resolve(value);
                };
                function withdraw(): void {
                    if (resolverRef.current === settle) {
                        answer(fallback);
                    }
                }
                signal?.addEventListener('abort', withdraw);
                resolverRef.current = settle;
                setQuestion({ ...next, open: true });
            });
        },
        [fallback, answer]
    );

    useEffect(() => {
        unmountedRef.current = false;
        return () => {
            unmountedRef.current = true;
            const resolve = resolverRef.current;
            resolverRef.current = null;
            resolve?.(fallback);
        };
    }, [fallback]);

    return { question, ask, answer };
}
